import { useState, useEffect, useRef, useCallback } from 'react';
import { messages as msgApi, delivery as deliveryApi } from '../api/client.js';
import { plans as plansApi } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { SendIcon, PaperclipIcon, CheckIcon } from '../common/icons.jsx';

const POLL_INTERVAL = 10000;

/**
 * Chat view for coordinator-driver messages.
 * Modern messaging UI with bubbles, read indicators, date separators.
 */
export default function ChatView() {
  const { user } = useAuth();
  const [messageList, setMessageList] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [planList, setPlanList] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    plansApi.list().then(setPlanList).catch(() => {});
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const params = {};
      if (selectedPlanId) params.planId = selectedPlanId;
      const data = await msgApi.list(params);
      setMessageList(data);
      for (const msg of data) {
        if (msg.sender_id !== user?.id && !msg.read_at) {
          msgApi.markRead(msg.id).catch(() => {});
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedPlanId, user?.id]);

  useEffect(() => {
    loadMessages();
    intervalRef.current = setInterval(loadMessages, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [loadMessages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messageList]);

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !attachmentUrl) || sending) return;
    setSending(true);
    setError('');
    try {
      await msgApi.send(text.trim() || '(photo)', selectedPlanId || null, attachmentUrl);
      setText('');
      setAttachmentUrl(null);
      await loadMessages();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const result = await deliveryApi.uploadPhoto(file, 'chat');
      setAttachmentUrl(result.url);
    } catch (err) {
      setError('Echec upload photo: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-16rem)] md:h-[calc(100vh-10rem)]">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-xl font-bold text-[#0f172a]">Chat équipe</h2>
        <select
          value={selectedPlanId}
          onChange={(e) => setSelectedPlanId(e.target.value)}
          className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-[#1e40af] focus:border-transparent outline-none"
        >
          <option value="">Toutes les tournées</option>
          {planList.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {new Date(plan.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} - {plan.driver_name}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="text-red-600 text-sm mb-2 bg-red-50 rounded-lg px-3 py-1.5">{error}</p>}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto bg-[#f8fafc] rounded-2xl p-4 space-y-1 mb-3 shadow-inner">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-6 w-6 border-3 border-[#1e40af] border-t-transparent rounded-full" />
          </div>
        ) : messageList.length === 0 ? (
          <p className="text-[#64748b] text-center py-8 text-sm">Aucun message. Commencez la conversation.</p>
        ) : (
          <MessageListWithDates messages={messageList} userId={user?.id} />
        )}
        <div ref={endRef} />
      </div>

      {/* Attachment preview */}
      {attachmentUrl && (
        <div className="flex items-center gap-2 mb-2 px-2 bg-gray-50 rounded-lg p-2">
          <img src={attachmentUrl} alt="piece jointe" className="h-12 rounded-lg object-cover" />
          <button onClick={() => setAttachmentUrl(null)}
            className="text-[#dc2626] text-xs font-medium hover:underline min-h-[44px] px-2">
            Retirer
          </button>
        </div>
      )}

      {/* Input bar */}
      <form onSubmit={handleSend} className="flex gap-2 items-end">
        <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handleFileSelect} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-gray-100 text-[#64748b] rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-colors"
          title="Joindre une photo"
        >
          <PaperclipIcon size={18} />
        </button>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Écrire un message..."
          className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e40af] focus:border-transparent outline-none text-sm transition-all"
          maxLength={1000}
        />
        <button
          type="submit"
          disabled={(!text.trim() && !attachmentUrl) || sending}
          className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-[#1e40af] text-white rounded-xl hover:bg-[#1e3a8a] disabled:opacity-40 disabled:hover:bg-[#1e40af] transition-colors"
        >
          <SendIcon size={18} />
        </button>
      </form>
    </div>
  );
}

/**
 * Renders messages with date separators between different days.
 * @param {{ messages: object[], userId: string }} props
 */
function MessageListWithDates({ messages, userId }) {
  let lastDate = '';

  return messages.map((msg) => {
    const msgDate = new Date(msg.created_at).toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
    const showSeparator = msgDate !== lastDate;
    lastDate = msgDate;

    return (
      <div key={msg.id}>
        {showSeparator && (
          <div className="flex items-center gap-3 my-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-[#64748b] font-medium px-2 capitalize">{msgDate}</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
        )}
        <MessageBubble msg={msg} isOwn={msg.sender_id === userId} />
      </div>
    );
  });
}

/**
 * Single message bubble with modern styling and read indicator.
 * @param {{ msg: object, isOwn: boolean }} props
 */
function MessageBubble({ msg, isOwn }) {
  const time = new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const isRead = !!msg.read_at;

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-1`}>
      {!isOwn && (
        <div className="w-7 h-7 rounded-full bg-[#0891b2] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mr-2 mt-1">
          {(msg.sender_name || '?')[0].toUpperCase()}
        </div>
      )}
      <div className={`max-w-[75%] px-4 py-2.5 ${
        isOwn
          ? 'bg-[#1e40af] text-white rounded-2xl rounded-br-md'
          : 'bg-white text-[#0f172a] rounded-2xl rounded-bl-md shadow-sm border border-gray-100'
      }`}>
        {!isOwn && (
          <div className="text-xs font-semibold text-[#0891b2] mb-1">{msg.sender_name}</div>
        )}
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
        {msg.attachment_url && (
          <img src={msg.attachment_url} alt="piece jointe" className="mt-2 rounded-xl max-h-48 w-full object-cover" />
        )}
        <div className={`text-[10px] mt-1 flex items-center gap-1 justify-end ${
          isOwn ? 'text-blue-200' : 'text-[#64748b]'
        }`}>
          {time}
          {isOwn && (
            <span className="flex items-center">
              <CheckIcon size={10} className={isRead ? 'text-blue-300' : 'opacity-50'} />
              {isRead && <CheckIcon size={10} className="text-blue-300 -ml-1" />}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
